/* eslint-disable no-mixed-operators */
import classNames from 'classnames';

const headingMappings = {
  display: {
    element: 'div',
    weightTypes: ['condensed']
  },
  h1: {
    element: 'h1',
    weightTypes: ['bold', 'condensed']
  },
  h2: {
    element: 'h2',
    weightTypes: ['bold', 'condensed']
  },
  h3: {
    element: 'h3',
    weightTypes: ['bold', 'condensed']
  },
  h4: {
    element: 'h4',
    weightTypes: ['bold', 'condensed']
  },
  h5: {
    element: 'h5',
    weightTypes: ['bold', 'condensed']
  }
};

const bodyMappings = {
  'body-base': {
    element: 'p',
    weightTypes: ['regular', 'bold']
  },
  'body-lg': {
    element: 'p',
    weightTypes: ['regular', 'bold']
  },
  'body-xs': {
    element: 'p',
    weightTypes: ['regular', 'bold']
  }
};

const variantMapping = {
  ...headingMappings,
  ...bodyMappings
};

const colorClasses = {
  primary: 'sui-text-primary',
  subtle: 'sui-text-subtle',
  info: 'sui-text-info',
  success: 'sui-text-success',
  warning: 'sui-text-warning',
  danger: 'sui-text-danger',
  brand: 'sui-text-brand',
  inverse: 'sui-text-inverse',
  inactive: 'sui-text-inactive',
  none: ''
};

const typographyClasses = {
  heading: {
    bold: ({ height, variant, decoration }) => classNames('sui-font-bold', {
      'sui-text-7xl md:sui-text-8xl xl:sui-text-9xl': variant === 'h1',
      'sui-text-4xl md:sui-text-5xl xl:sui-text-7xl': variant === 'h2',
      'sui-text-3xl md:sui-text-4xl xl:sui-text-6xl': variant === 'h3',
      'sui-text-2xl md:sui-text-3xl xl:sui-text-5xl': variant === 'h4',
      'sui-text-xl xl:sui-text-3xl': variant === 'h5',
      'sui-leading-tight': height === 'tight',
      'sui-leading-normal': height === 'normal',
      'sui-underline': decoration === 'underline',
    }, 'sui-tracking-normal sui-normal-case'),
    condensed: ({ height, uppercase, variant, decoration }) => classNames('sui-font-condensed', {
      'sui-text-8xl md:sui-text-9xl xl:sui-text-10xl': variant === 'display',
      'sui-text-6xl md:sui-text-8xl xl:sui-text-9xl': variant === 'h1',
      'sui-text-5xl md:sui-text-6xl xl:sui-text-7xl': variant === 'h2',
      'sui-text-3xl md:sui-text-4xl xl:sui-text-6xl': variant === 'h3',
      'sui-text-2xl md:sui-text-3xl xl:sui-text-5xl': variant === 'h4',
      'sui-text-xl xl:sui-text-3xl': variant === 'h5',
      'sui-leading-snug': height === 'snug',
      'sui-leading-none': height === 'none',
      'sui-leading-relaxed': height === 'relaxed' && (variant === 'h4' || variant === 'h5'),
      'sui-uppercase': uppercase,
      'sui-normal-case': !uppercase,
      'sui-underline': decoration === 'underline',
    }, 'sui-tracking-wide'),
  },
  body: {
    regular: ({ variant, height, decoration }) => classNames('sui-font-regular', {
      'sui-text-base': variant === 'body-base',
      'sui-text-xs': variant === 'body-xs',
      'sui-text-lg': variant === 'body-lg',
      'sui-leading-tight': height === 'tight',
      'sui-leading-normal': height === 'normal',
      'sui-leading-loose': height === 'loose' && variant === 'body-base',
      'sui-underline': decoration === 'underline',
      'sui-line-through': decoration === 'line-through'
    }, 'sui-tracking-normal sui-normal-case'),
    bold: ({ variant, height, decoration }) => classNames('sui-font-bold', {
      'sui-text-base': variant === 'body-base',
      'sui-text-xs': variant === 'body-xs',
      'sui-text-lg': variant === 'body-lg',
      'sui-leading-tight': height === 'tight',
      'sui-leading-normal': height === 'normal',
      'sui-leading-loose': height === 'loose' && variant === 'body-base',
      'sui-underline': decoration === 'underline',
      'sui-line-through': decoration === 'line-through'
    }, 'sui-tracking-normal sui-normal-case')
  },
};

const lineClampMapping = {
  default: {
    1: 'sui-line-clamp-1',
    2: 'sui-line-clamp-2',
    3: 'sui-line-clamp-3',
    4: 'sui-line-clamp-4',
    5: 'sui-line-clamp-5',
    6: 'sui-line-clamp-6',
    none: 'sui-line-clamp-none',
  },
  sm: {
    1: 'sm:sui-line-clamp-1',
    2: 'sm:sui-line-clamp-2',
    3: 'sm:sui-line-clamp-3',
    4: 'sm:sui-line-clamp-4',
    5: 'sm:sui-line-clamp-5',
    6: 'sm:sui-line-clamp-6',
    none: 'sm:sui-line-clamp-none',
  },
  md: {
    1: 'md:sui-line-clamp-1',
    2: 'md:sui-line-clamp-2',
    3: 'md:sui-line-clamp-3',
    4: 'md:sui-line-clamp-4',
    5: 'md:sui-line-clamp-5',
    6: 'md:sui-line-clamp-6',
    none: 'md:sui-line-clamp-none',
  },
  lg: {
    1: 'lg:sui-line-clamp-1',
    2: 'lg:sui-line-clamp-2',
    3: 'lg:sui-line-clamp-3',
    4: 'lg:sui-line-clamp-4',
    5: 'lg:sui-line-clamp-5',
    6: 'lg:sui-line-clamp-6',
    none: 'lg:sui-line-clamp-none',
  },
  xl: {
    1: 'xl:sui-line-clamp-1',
    2: 'xl:sui-line-clamp-2',
    3: 'xl:sui-line-clamp-3',
    4: 'xl:sui-line-clamp-4',
    5: 'xl:sui-line-clamp-5',
    6: 'xl:sui-line-clamp-6',
    none: 'xl:sui-line-clamp-none',
  },
  '2xl': {
    1: '2xl:sui-line-clamp-1',
    2: '2xl:sui-line-clamp-2',
    3: '2xl:sui-line-clamp-3',
    4: '2xl:sui-line-clamp-4',
    5: '2xl:sui-line-clamp-5',
    6: '2xl:sui-line-clamp-6',
    none: '2xl:sui-line-clamp-none',
  }
};

const expandClamp = (value = 'none') => {
  if (typeof value === 'object') {
    return Object.entries(value).map(([breakpoint, lines]) => {
      return lineClampMapping[breakpoint][lines];
    }).join(' ');
  }
  return lineClampMapping.default[value];
};

export default function createTypography({
  color = 'primary',
  decoration,
  height,
  uppercase,
  lineClamp: lineClampProp,
  variant: variantProp,
  weight: weightProp
}) {

  const variant = variantMapping[variantProp] !== undefined ? variantProp : 'body-base';

  const isHeading = headingMappings[variant] !== undefined;

  const { element, weightTypes } = variantMapping[variant];

  const weight = weightTypes.indexOf(weightProp) !== -1 ? weightProp : weightTypes[0];

  const typeClasses = typographyClasses[isHeading ? 'heading' : 'body']?.[weight]?.({ height, variant, uppercase, decoration });

  const lineClamp = expandClamp(lineClampProp);

  const classes = `sui-antialiased ${typeClasses} ${colorClasses[color]} ${lineClamp}`;

  return {
    classes,
    element
  };
}